import fonts from 'styles/typography.module.css';
import { Dialog, DialogContent } from '@mui/material';
import { ModalHeader } from 'layouts/Generic/modal-header';
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import moment from 'moment';
import { Filter, sendSmartReport } from 'services/clients/perDiem';
import { useSegment } from 'common/hooks/segment';
import { PerDiemArchiveMode } from 'pages/PerDiemArchive/types';
import { DateTime } from 'luxon';
import { Error, Form, Loading, Success, Upgrade } from './stages';

interface Props {
  open: boolean;
  onClose: () => void;
  filter: Filter;
  feature: string;
  selectedContainer?: string;
}

const HEADER_DATE_FORMAT = 'MM/DD/YYYY';

export const DownloadModal: React.FC<Props> = (props) => {
  const { track } = useSegment();
  const { open, filter, onClose, feature, selectedContainer } = props;

  const downloadMutation = useMutation(sendSmartReport);

  let inner = (
    <Form
      defaultContainers={selectedContainer ? [selectedContainer] : []}
      onSubmit={(values) =>
        downloadMutation.mutate(
          {
            filter,
            ...values,
          },
          {
            onSettled: (data, error, variables) => {
              track(
                'Generate-report Button Clicked',
                {
                  feature,
                  selectedRange: PerDiemArchiveMode.SmartPerDiem,
                  selectedEmails: variables.emails || [],
                  reportPreference: 'email',
                  startDate: variables.filter.startDate,
                  endDate: variables.filter.endDate,
                  containerTypeKey: variables.filter.containerType,
                  shippingLineKey: variables.filter.shippingLine,
                  numberOfDays: DateTime.fromISO(variables.filter.endDate).diff(
                    DateTime.fromISO(variables.filter.startDate),
                    'days'
                  ),
                  selectedTime: '',
                  containerNumbers: variables.containers || [],
                  proof: variables.proof,
                  downloadChecked: true,
                },
                { includeUser: true }
              );
            },
          }
        )
      }
    />
  );
  if (downloadMutation.isLoading) {
    inner = <Loading />;
  } else if (downloadMutation.isError) {
    inner = (
      <Error
        onClose={onClose}
        title="There was an error with your report"
        message="We apologize for the inconvenience. Please try to generate your report again."
      />
    );
  } else if (downloadMutation.isSuccess) {
    if (downloadMutation.data.code === 'no_credits') {
      inner = <Upgrade />;
    } else {
      inner = <Success onClose={onClose} />;
    }
  }

  React.useEffect(() => {
    downloadMutation.reset();
    // eslint-disable-next-line
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: 'w-[960px] max-w-[80%]' }}
      className={fonts.font}
    >
      <ModalHeader onClose={onClose}>
        {/* TODO: Update this header if needs upgrade  */}
        <p className="font-bold">
          Finalize your Per Diem report - {filter.shippingLine} - {filter.containerType} -{' '}
          {moment(filter.startDate).format(HEADER_DATE_FORMAT)} to{' '}
          {moment(filter.endDate).subtract(1, 'days').format(HEADER_DATE_FORMAT)}
        </p>
      </ModalHeader>
      <DialogContent className="p-8" dividers>
        {inner}
      </DialogContent>
    </Dialog>
  );
};
DownloadModal.displayName = 'DownloadModal';
